"use client";

import { motion } from "motion/react";
import { useTranslation } from "react-i18next";

import { DocxFileView } from "@/components/lazy-heavy-components";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import type { AsidePanelResize } from "@/hooks/use-aside-view";
import { useIsMobile } from "@/hooks/use-media-query";

export interface DocxFileViewerPanelProps {
  /** Signed file-download capability URL. */
  url: string;
  /** Filename supplied by the inline link or attachment. */
  fileName: string;
  onClose: () => void;
  onOpenInNewWindow?: () => void;
  /** Supplied by the shell's aside manager for every in-app mount. */
  resize: AsidePanelResize;
}

/** Responsive DOCX file-link viewer: full-screen on mobile, aside on desktop. */
export function DocxFileViewerPanel({
  url,
  fileName,
  onClose,
  onOpenInNewWindow,
  resize,
}: DocxFileViewerPanelProps) {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const content = (
    <DocxFileView
      url={url}
      fileName={fileName}
      onClose={onClose}
      onOpenInNewTab={onOpenInNewWindow}
      className="h-full"
    />
  );

  if (isMobile) {
    return (
      <Dialog open={true} onOpenChange={(open) => { if (!open) onClose(); }}>
        <DialogContent
          className="flex h-full max-h-full w-full max-w-full flex-col gap-0 overflow-hidden rounded-none p-0"
          hideCloseButton
          data-testid="docx-file-viewer-dialog"
        >
          <DialogTitle className="sr-only">{fileName}</DialogTitle>
          {content}
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <motion.aside
      initial={{ x: "100%" }}
      animate={{ x: 0 }}
      exit={{ x: "100%" }}
      transition={{ type: "spring", damping: 30, stiffness: 300 }}
      className="relative flex h-full min-w-0 shrink-0 flex-col overflow-hidden border-l bg-background"
      style={{ width: resize.width }}
      data-testid="docx-file-viewer-panel"
    >
      <div
        role="separator"
        aria-orientation="vertical"
        aria-label={t("viewer.dragToResize")}
        onPointerDown={resize.onPointerDown}
        onPointerMove={resize.onPointerMove}
        onPointerUp={resize.onPointerUp}
        onDoubleClick={resize.onDoubleClick}
        className="absolute -left-[3px] top-0 z-20 h-full w-1.5 touch-none cursor-col-resize select-none transition-colors hover:bg-primary/30 active:bg-primary/40"
      />
      {content}
    </motion.aside>
  );
}
